// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { CHECKOUT_STEPS, findStep } from "@steiner/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StepperContext, withStepperContext } from "@steiner/App/Widgets/CheckoutStepper/StepperContext";
import Check from "@steiner/XDL/Icons/Check";
import Circle from "@steiner/XDL/Icons/Circle";
import { xClassNames } from "@steiner/XDL/Utilities";
import React, { useContext } from "react";

interface OwnProps {}

type Props = OwnProps;

export interface CheckoutStepperItemsStyles {
    wrapper?: InjectableCss;
    insideWrapper?: InjectableCss;
    stepNumber?: InjectableCss;
    stepIcon?: IconPresentationProps;
    title?: TypographyProps;
    icon?: TypographyProps;
}

export interface CheckoutStepperStyles {
    container?: InjectableCss;
    stepperWrapper?: InjectableCss;
    stepperItem?: CheckoutStepperItemsStyles;
}

export const checkoutStepperStyles: CheckoutStepperStyles = {};

const styles = checkoutStepperStyles;
export const CheckoutStepper: React.FC<Props> = () => {
    const { isOrderConfirmationPage } = useAppSelector(state => ({
        isOrderConfirmationPage: getCurrentPage(state).type === "OrderConfirmationPage",
    }));

    const { steps, currentStep, moveToStepHandler } = useContext(StepperContext);
    const currentStepText = findStep(steps, currentStep).name;

    const StepperComponent = () => {
        const style = styles.stepperItem || {};
        return (
            <Typography as="ul" {...styles.stepperWrapper}>
                {steps.map((item, index) => {
                    const isCompleted = item.index < currentStep;
                    const isActive = item.index === currentStep;
                    const isClickable = !isOrderConfirmationPage && item.index !== CHECKOUT_STEPS.CONFIRMATION;

                    const props = { isCompleted, isActive, isClickable };
                    const className = xClassNames.append([
                        isActive && "active",
                        isCompleted && "completed",
                        isClickable ? "clickable" : "not-clickable",
                    ]);

                    return (
                        <Typography
                            as="li"
                            {...style.wrapper}
                            {...props}
                            key={item.name}
                            className={className}
                            onClick={() => isClickable && moveToStepHandler(item.index)}
                            role="listitem"
                            aria-label="Move to Step Clickable"
                        >
                            <StyledWrapper {...style.insideWrapper} {...props} className="inside-wrapper">
                                <Typography {...style.icon} className={`stepper-icon ${className}`}>
                                    {isActive && <Icon src={Circle} size={15} color="common.ui8" />}
                                    {isCompleted && <Icon src={Check} size={8} color="common.ui8" />}
                                </Typography>
                                <Typography {...style.title} {...props} className={className}>
                                    {item.displayName || item.name}
                                </Typography>
                            </StyledWrapper>
                        </Typography>
                    );
                })}
            </Typography>
        );
    };

    return (
        <StyledWrapper {...styles.container}>
            <VisuallyHidden aria-current="step">{translate(currentStepText)}</VisuallyHidden>
            <StepperComponent />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withStepperContext(CheckoutStepper),
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Checkout Stepper",
        fieldDefinitions: [],
    },
};

export default widgetModule;
